import OperatorShell from "@/components/operator/OperatorShell";
import { DAILY_ACTIVITY, HEATMAP, HOURS, type Rep } from "@/lib/extended";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import DualGoalProgressBar from "@/components/DualGoalProgressBar";
import SprintBoyToggle from "@/components/SprintBoyToggle";
import SprintActivityMath from "@/components/SprintActivityMath";
import { getCurrentTenant } from "@/lib/session";

export default function Daily() {
  const tenant = getCurrentTenant();
  const isDemo = tenant?.is_demo === true;

  // For production tenants, show empty state — real activity will come from leads_activity table
  if (!isDemo) {
    return (
      <OperatorShell breadcrumbs={[{ label: "Daily" }]} pageTitle="Daily" pageMeta="Daily snapshot">
        <div className="card-shell p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Activity log fills as your team logs daily outreach.
          </p>
        </div>
      </OperatorShell>
    );
  }

  // Aggregated totals (demo only)
  const totals = DAILY_ACTIVITY.reduce(
    (acc, r) => ({
      calls: acc.calls + r.calls,
      emails: acc.emails + r.emails,
      meetings: acc.meetings + r.meetings,
      cesIdentified: acc.cesIdentified + r.cesIdentified,
      stakeholderMaps: acc.stakeholderMaps + r.stakeholderMaps,
      plansInFlight: acc.plansInFlight + r.plansInFlight,
      closedWon: acc.closedWon + r.closedWon,
      closedLost: acc.closedLost + r.closedLost,
      slipped: acc.slipped + r.slipped,
    }),
    {
      calls: 0,
      emails: 0,
      meetings: 0,
      cesIdentified: 0,
      stakeholderMaps: 0,
      plansInFlight: 0,
      closedWon: 0,
      closedLost: 0,
      slipped: 0,
    },
  );

  return (
    <OperatorShell breadcrumbs={[{ label: "Daily" }]} pageTitle="Daily" pageMeta="Daily snapshot · what we measure">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
            Daily metrics report
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Yesterday in numbers</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Activity, quality, outcomes. One row per ae. Words talk, numbers scream.
          </p>
        </div>
        <SprintBoyToggle />
      </div>

      <div className="mb-6 grid gap-3 md:grid-cols-2">
        <DualGoalProgressBar />
        <SprintActivityMath />
      </div>

      {/* Top tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Tile label="Calls" value={totals.calls.toString()} />
        <Tile label="Emails" value={totals.emails.toString()} />
        <Tile label="Meetings" value={totals.meetings.toString()} />
        <Tile label="Firm / formal timelines identified" value={totals.cesIdentified.toString()} />
      </div>

      {/* Activity */}
      <Section title="Activity" subtitle="Calls, emails and meetings — yesterday vs 7-day average">
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left px-2 py-2 font-medium">Rep</th>
                <th className="text-right px-2 py-2 font-medium">Calls</th>
                <th className="text-right px-2 py-2 font-medium">Emails</th>
                <th className="text-right px-2 py-2 font-medium">Meetings</th>
              </tr>
            </thead>
            <tbody>
              {DAILY_ACTIVITY.map((r) => (
                <tr key={r.rep} className="border-b border-border last:border-0">
                  <td className="px-2 py-3 text-foreground">{r.rep}</td>
                  <td className="px-2 py-3 text-right">
                    <DeltaCell value={r.calls} avg={r.callsAvg7} />
                  </td>
                  <td className="px-2 py-3 text-right">
                    <DeltaCell value={r.emails} avg={r.emailsAvg7} />
                  </td>
                  <td className="px-2 py-3 text-right">
                    <DeltaCell value={r.meetings} avg={r.meetingsAvg7} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Quality */}
      <Section title="Quality" subtitle="The work that produces the result">
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left px-2 py-2 font-medium">Rep</th>
                <th className="text-right px-2 py-2 font-medium">CEs identified</th>
                <th className="text-right px-2 py-2 font-medium">Stakeholder maps built</th>
                <th className="text-right px-2 py-2 font-medium">Plans in flight</th>
              </tr>
            </thead>
            <tbody>
              {DAILY_ACTIVITY.map((r) => (
                <tr key={r.rep} className="border-b border-border last:border-0">
                  <td className="px-2 py-3 text-foreground">{r.rep}</td>
                  <td className="px-2 py-3 text-right tabular text-muted-foreground">{r.cesIdentified}</td>
                  <td className="px-2 py-3 text-right tabular text-muted-foreground">{r.stakeholderMaps}</td>
                  <td className="px-2 py-3 text-right tabular text-muted-foreground">{r.plansInFlight}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Outcomes */}
      <Section title="Outcomes" subtitle="Closed won, closed lost, slipped">
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left px-2 py-2 font-medium">Rep</th>
                <th className="text-right px-2 py-2 font-medium">Closed won</th>
                <th className="text-right px-2 py-2 font-medium">Closed lost</th>
                <th className="text-right px-2 py-2 font-medium">Slipped</th>
              </tr>
            </thead>
            <tbody>
              {DAILY_ACTIVITY.map((r) => (
                <tr key={r.rep} className="border-b border-border last:border-0">
                  <td className="px-2 py-3 text-foreground">{r.rep}</td>
                  <td className="px-2 py-3 text-right tabular text-emerald-300">{r.closedWon}</td>
                  <td className="px-2 py-3 text-right tabular text-rose-300">{r.closedLost}</td>
                  <td className="px-2 py-3 text-right tabular text-orange-300">{r.slipped}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Heatmap */}
      <Section title="Time-of-day activity" subtitle="When the work happens">
        <Heatmap />
      </Section>
    </OperatorShell>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="card-shell-2 px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold tabular tracking-tight mt-1">{value}</div>
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card-shell p-5 mb-4">
      <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">{title}</div>
      {subtitle && (
        <div className="text-[13px] text-muted-foreground mb-3 mt-0.5">{subtitle}</div>
      )}
      {children}
    </div>
  );
}

function DeltaCell({ value, avg }: { value: number; avg: number }) {
  const delta = value - avg;
  const up = delta > 0;
  const flat = delta === 0;
  return (
    <div className="inline-flex items-center gap-2 justify-end">
      <span className="tabular text-foreground w-8 text-right">{value}</span>
      <span
        className={`tabular text-[11px] inline-flex items-center gap-0.5 w-12 ${
          flat ? "text-muted-foreground" : up ? "text-emerald-300" : "text-rose-300"
        }`}
      >
        {flat ? null : up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
        {flat ? "—" : `${up ? "+" : ""}${delta}`}
      </span>
    </div>
  );
}

function Heatmap() {
  const reps = Object.keys(HEATMAP) as Rep[];
  return (
    <div className="overflow-x-auto -mx-5 px-5">
      <table className="text-xs min-w-[560px]">
        <thead>
          <tr className="text-muted-foreground">
            <th className="text-left px-2 py-1 font-medium uppercase tracking-wider text-[10px]">Rep</th>
            {HOURS.map((h) => (
              <th key={h} className="px-1 py-1 font-medium text-[10px] tabular text-right">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {reps.map((r) => (
            <tr key={r}>
              <td className="px-2 py-1 text-foreground whitespace-nowrap">{r}</td>
              {HEATMAP[r].map((v, i) => (
                <td key={i} className="px-1 py-1">
                  <div
                    className="w-7 h-6 rounded-sm"
                    style={{
                      background: `hsla(358, 67%, 50%, ${(v / 10).toFixed(2)})`,
                      border: "1px solid hsl(var(--border))",
                    }}
                    title={`${v}/10`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-2 mt-3 text-[11px] text-muted-foreground">
        <span>low</span>
        {[0.1, 0.25, 0.5, 0.75, 1].map((o, i) => (
          <div
            key={i}
            className="w-5 h-3 rounded-sm"
            style={{ background: `hsla(358, 67%, 50%, ${o})`, border: "1px solid hsl(var(--border))" }}
          />
        ))}
        <span>high</span>
      </div>
    </div>
  );
}
